import { Component, OnDestroy } from '@angular/core';

import { ReplaySubject } from 'rxjs';

import { SharedLanguageService } from '@language';
import {
    ExtendedProfileField,
    FieldReadHelper,
    GenericTypedValue,
    PartialRecord,
    ShComplexReadonlyFieldComponent,
    ShSearchRoutingService,
    SimpleFieldValue
} from '@stylehaven/components';
import { TranslateOrDefault } from '../../../../helpers';
import { GetProfileChildFieldLabels } from '../../../helpers/get-profile-field-label.helper';

@Component({
    standalone: false,
    selector: 'digihaven-retention-period-readonly-field',
    templateUrl: './retention-period-readonly-field.component.html',
    styleUrls: ['../../base-generic-readonly-field/base-generic-readonly-field.component.scss']
})
export class RetentionPeriodReadonlyFieldComponent extends ShComplexReadonlyFieldComponent implements OnDestroy {
    public typeTranslations!: Map<string, string>;

    public isRetentionPeriodSet$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    public definedIcon$: ReplaySubject<string> = new ReplaySubject<string>(1);
    public value$: ReplaySubject<string> = new ReplaySubject<string>(1);
    public specification$: ReplaySubject<string> = new ReplaySubject<string>(1);
    public info$: ReplaySubject<string> = new ReplaySubject<string>(1);
    public motivation$: ReplaySubject<string> = new ReplaySubject<string>(1);

    private EMPTY_PLACEHOLDER = '-';

    constructor(
        protected fieldHelper: FieldReadHelper,
        protected shSearchRoutingService: ShSearchRoutingService,
        protected translateService: SharedLanguageService
    ) {
        super(fieldHelper, shSearchRoutingService);
    }

    init(profileField: ExtendedProfileField, record: PartialRecord): void {
        this.typeTranslations = this.getTranslations(profileField);
        const values: GenericTypedValue<SimpleFieldValue>[] =
            this.fieldHelper.getMapFieldValue(
                profileField.FieldDefinition,
                record,
                profileField.FieldDefinition.Children?.map(child => child.Key) ?? []
            ) ?? [];

        const term: string = this.getValue(values, 'Term');
        const unit: string = this.getValue(values, 'Unit');
        const specification = this.getValue(values, 'Specification');
        const info = this.getValue(values, 'Info');
        const motivation = this.getValue(values, 'Motivation');

        const fallbackDefined = [term, unit, specification, info, motivation].some(
            value => value !== this.EMPTY_PLACEHOLDER
        );

        const defined =
            values?.filter(value => value.type === 'Defined').map(value => value.value as boolean)[0] ||
            fallbackDefined ||
            false;
        this.isRetentionPeriodSet$.next(defined);
        this.definedIcon$.next(defined ? 'check' : 'close');

        const value = `${term} ${unit}`;
        this.value$.next(value);
        this.specification$.next(specification);
        this.info$.next(info);
        this.motivation$.next(motivation);
    }

    private getTranslations(profileField: ExtendedProfileField): Map<string, string> {
        const typeTranslations = GetProfileChildFieldLabels(this.translateService, profileField);
        // 'Defined' has no field definition, but still needs labels
        const definedTranslation: string = TranslateOrDefault(
            this.translateService,
            `overrides.${profileField.FieldDefinition.DottedKey}.Defined`
        );
        typeTranslations.set('Defined', definedTranslation);
        return typeTranslations;
    }

    private getValue(values: GenericTypedValue<SimpleFieldValue>[], key: string): string {
        return (
            values?.filter(value => value.type === key).map(value => value.value as string)[0] || this.EMPTY_PLACEHOLDER
        );
    }

    public ngOnDestroy(): void {
        this.isRetentionPeriodSet$.complete();
        this.value$.complete();
        this.specification$.complete();
        this.info$.complete();
        this.motivation$.complete();
        super.ngOnDestroy();
    }
}
